import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  article?: { publishedTime?: string; section?: string };
}

const BASE_URL = "https://themetroshine.com";

const pageMeta: Record<string, { title: string; description: string }> = {
  "/": {
    title: "Metroshine – Luxury Mobile Car Detailing | DC, MD, VA",
    description: "Premium mobile car detailing serving Washington DC, Maryland & Virginia. Interior, exterior, paint correction & ceramic sealant protection. We come to you. Book today.",
  },
  "/services": {
    title: "Mobile Detailing Services | Metroshine – DC, MD, VA",
    description: "Explore Metroshine's full menu of mobile detailing services: interior deep clean, exterior polish, ceramic sealant, paint correction & more in DC, MD, VA.",
  },
  "/pricing": {
    title: "Car Detailing Prices & Packages | Metroshine – DC, MD, VA",
    description: "Transparent mobile detailing pricing. Essential, Signature & Elite packages for interior, exterior & full details. Coupes to Large SUVs. Starting at $118.99.",
  },
  "/gallery": {
    title: "Before & After Detailing Gallery | Metroshine – DC, MD, VA",
    description: "See real before & after results from Metroshine's luxury mobile detailing. Interior, exterior, wheel cleaning, and full detail transformations.",
  },
  "/about": {
    title: "About Metroshine | Founded at 15 by Omer & Davin – Vienna, VA",
    description: "Meet Omer (Owner) & Davin (Co-Owner), who started Metroshine at age 15. Learn our story and commitment to elite mobile car detailing in the DMV.",
  },
  "/reviews": {
    title: "5-Star Customer Reviews | Metroshine Mobile Detailing – DC, MD, VA",
    description: "Read real 5-star Google reviews from Metroshine customers across DC, Maryland & Virginia. See why hundreds trust us with their luxury vehicles.",
  },
  "/book": {
    title: "Book Mobile Car Detailing | Metroshine – DC, MD, VA",
    description: "Schedule your premium mobile car detailing appointment. We come to your home or office in DC, Maryland & Virginia. Same-week availability. Book now.",
  },
  "/blog": {
    title: "Car Detailing Tips & Guides | Metroshine Blog – DC, MD, VA",
    description: "Expert car detailing tips, paint correction guides, ceramic sealant info, and mobile detailing comparisons from Metroshine's pros in DC, MD & VA.",
  },
  "/dealership-fleet": {
    title: "Dealership and Fleet Detailing in DMV | Metroshine",
    description: "Mobile dealership and fleet detailing across DC, Maryland, and Virginia. Fast turnaround, volume pricing, and consistent results.",
  },
  "/terms": {
    title: "Terms & Policies | Metroshine – DC, MD, VA",
    description: "Metroshine's terms and policies for luxury mobile car detailing in Washington DC, Maryland & Virginia. Clear expectations for booking, pricing, and service.",
  },
};

const SEOHead = ({ title, description, canonical, ogImage, ogType, article }: SEOHeadProps) => {
  const location = useLocation();
  const path = location.pathname;

  // For blog posts, derive from path
  const isBlogPost = path.startsWith("/blog/") && path !== "/blog";
  const meta = pageMeta[path] || pageMeta["/"];

  const pageTitle = title || meta.title;
  const pageDescription = description || meta.description;
  const pageCanonical = canonical || `${BASE_URL}${path === "/" ? "" : path}`;
  const pageOgImage = ogImage || `${BASE_URL}/og-image.jpg`;
  const pageOgType = ogType || (isBlogPost ? "article" : "website");

  useEffect(() => {
    document.title = pageTitle;

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta("description", pageDescription);
    setMeta("og:title", pageTitle, "property");
    setMeta("og:description", pageDescription, "property");
    setMeta("og:url", pageCanonical, "property");
    setMeta("og:type", pageOgType, "property");
    setMeta("og:image", pageOgImage, "property");
    setMeta("og:image:width", "1200", "property");
    setMeta("og:image:height", "630", "property");
    setMeta("og:site_name", "Metroshine", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", pageTitle);
    setMeta("twitter:description", pageDescription);
    setMeta("twitter:image", pageOgImage);

    // Article-specific meta
    if (article?.publishedTime) {
      setMeta("article:published_time", article.publishedTime, "property");
    }
    if (article?.section) {
      setMeta("article:section", article.section, "property");
    }

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = pageCanonical;
  }, [pageTitle, pageDescription, pageCanonical, pageOgImage, pageOgType, article]);

  return null;
};

export default SEOHead;
